import { css } from '@emotion/css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DataSourceJsonData, DataSourceSettings, GrafanaTheme2 } from '@grafana/data';
import { ConfigSection, ConfigSubSection } from '@grafana/experimental';
import { getBackendSrv } from '@grafana/runtime';
import {
  DeleteButton,
  useStyles2,
  VerticalGroup,
  IconButton,
  Button,
  HorizontalGroup,
  InlineField,
  InlineFieldRow,
  Input,
} from '@grafana/ui';
import { ConfigDescriptionLink } from 'app/core/components/ConfigDescriptionLink';
import { contextSrv } from 'app/core/core';
import { DataSourceReadOnlyMessage } from 'app/features/datasources/components/DataSourceReadOnlyMessage';
import { Team } from 'app/types';

import { AccessControlAction as EnterpriseActions, TeamHeaders, TeamLBACRule } from '../types';

import { AddTeamLBACForm, LBACFormData } from './AddTeamLBACForm';
import { EditableCell } from './EditableCell';
import { TeamLabel } from './TeamLabel';
import { extractLBACRule, formatLBACRule, LBACHTTPHeaderName, validateLBACRule } from './utils';

export interface Props {
  teamHeaders: TeamHeaders;
  dataSourceConfig: DataSourceSettings<DataSourceJsonData, {}>;
  readOnly?: boolean;
  onTeamHeadersUpdate: (headers: TeamHeaders) => Promise<DataSourceSettings> | void;
}

export const TeamLBACEditor = ({ teamHeaders = {}, dataSourceConfig, readOnly, onTeamHeadersUpdate }: Props) => {
  const [rowsEditing, setRowsEditing] = useState<Record<number, boolean>>({});
  const [rowsError, setRowsError] = useState<Record<number, string>>({});
  const [teams, setTeams] = useState<Array<Partial<Team>>>([]);
  const [showLBACForm, setShowLBACForm] = useState(false);
  const [rowsToUpdate, setRowsToUpdate] = useState<Record<number, TeamLBACRule>>({});
  const [defaultRule, setDefaultRule] = useState('');
  const styles = useStyles2(getStyles);

  useEffect(() => {
    const fetchTeams = async () => {
      const teamIds = Object.keys(teamHeaders);
      if (!teamIds?.length) {
        return;
      }

      const result = await getBackendSrv().get('/api/teams/search', { teamId: teamIds });
      const teamsArray: Team[] = result?.teams;
      const teams = teamsArray.map((team) => ({
        id: team.id,
        value: team.id,
        teamId: team.id,
        name: team.name,
        avatarUrl: team.avatarUrl,
      }));
      setTeams(teams);
    };

    fetchTeams();
    if (teamHeaders['default'] && teamHeaders['default'].length) {
      const defaultLBACRule = extractLBACRule(teamHeaders['default'][0].value);
      setDefaultRule(defaultLBACRule);
    }
  }, [teamHeaders]);

  const teamsHeaders = useMemo(() => {
    const teamRules: TeamLBACRule[] = [];
    let recordId = 0;
    Object.keys(teamHeaders)?.forEach((key) => {
      if (key === 'default') {
        return;
      }

      const teamId = Number(key);
      teamHeaders[teamId].forEach((h) => {
        teamRules.push({
          id: recordId,
          teamId: teamId,
          labelSelector: extractLBACRule(h.value),
        });
        recordId++;
      });
    });
    return teamRules;
  }, [teamHeaders]);

  const toTeamHeaders = useCallback(
    (teamsLBACRules: TeamLBACRule[]): TeamHeaders => {
      const headers: TeamHeaders = {};
      const tenantId = dataSourceConfig.basicAuthUser;
      teamsLBACRules.forEach((rule, i) => {
        const header = { header: LBACHTTPHeaderName, value: formatLBACRule(rule.labelSelector, tenantId) };
        headers[rule.teamId] = headers[rule.teamId] ? headers[rule.teamId].concat(header) : [header];
      });
      return headers;
    },
    [dataSourceConfig]
  );

  const onTeamHeadersUpdateInternal = useCallback(
    async (headers: TeamHeaders) => {
      if (defaultRule) {
        const tenantId = dataSourceConfig.basicAuthUser;
        const defaultHeader = { header: LBACHTTPHeaderName, value: formatLBACRule(defaultRule, tenantId) };
        headers['default'] = [defaultHeader];
      }
      return onTeamHeadersUpdate(headers);
    },
    [onTeamHeadersUpdate, dataSourceConfig, defaultRule]
  );

  const onSaveHeader = useCallback(
    (rule: TeamLBACRule) => {
      const newHeaders = [...teamsHeaders];
      if (rowsToUpdate[rule.id]) {
        newHeaders[rule.id] = rowsToUpdate[rule.id];
        if (!validateLBACRule(newHeaders[rule.id].labelSelector)) {
          setRowsError({ ...rowsError, [rule.id]: 'Invalid label selector' });
          return;
        }
        onTeamHeadersUpdateInternal({ ...toTeamHeaders(newHeaders) });
      }
      setRowsEditing({ ...rowsEditing, [rule.id]: false });
    },
    [onTeamHeadersUpdateInternal, teamsHeaders, toTeamHeaders, rowsToUpdate, rowsError, rowsEditing]
  );

  const onDeleteHeader = useCallback(
    (id: number) => {
      const newHeaders = [...teamsHeaders];
      newHeaders.splice(id, 1);
      onTeamHeadersUpdateInternal({ ...toTeamHeaders(newHeaders) });
    },
    [teamsHeaders, onTeamHeadersUpdateInternal, toTeamHeaders]
  );

  const onSubmitLBAC = async ({ team, rule }: LBACFormData) => {
    const newHeaders = { ...teamHeaders };
    const tenantId = dataSourceConfig.basicAuthUser;
    const header = { header: LBACHTTPHeaderName, value: formatLBACRule(rule, tenantId) };
    newHeaders[team] = newHeaders[team] ? newHeaders[team].concat(header) : [header];
    await onTeamHeadersUpdateInternal({ ...newHeaders });
    setShowLBACForm(false);
  };

  const onEditRow = useCallback(
    (id: number, value: TeamLBACRule) => {
      setRowsToUpdate({ ...rowsToUpdate, [id]: value });
    },
    [rowsToUpdate]
  );

  const onDefaultRuleChange = (event: React.FormEvent<HTMLInputElement>) => {
    const newRule = event.currentTarget.value;
    setDefaultRule(newRule);
  };

  const onSaveDefaultRule = () => {
    onTeamHeadersUpdateInternal({ ...teamHeaders });
  };

  const canEdit = contextSrv.hasPermission(EnterpriseActions.DataSourcesPermissionsWrite) && !readOnly;

  return (
    <div>
      {readOnly && <DataSourceReadOnlyMessage />}
      <VerticalGroup spacing={'md'}>
        <ConfigSection
          className={styles.sectionHeader}
          title="Team LBAC"
          description="Team LBAC allows you to configure access to logs based on each user's team membership. Users wanting fine grained access to data sources such as Loki with X amount of teams with different levels of access can make use of Team LBAC."
        >
          <HorizontalGroup>
            <ConfigSubSection
              title="Default Rule"
              description={
                <ConfigDescriptionLink
                  description="Apply a default rule that would be used for all users without a team LBAC rule."
                  suffix="loki/configure-loki-data-source/#team-lbac-default-rule"
                  feature="lbac default rule"
                />
              }
            >
              <div className={styles.container}>
                <InlineFieldRow className={styles.row}>
                  <InlineField label="Default rule" disabled={!canEdit}>
                    <Input
                      className="width-20"
                      id="advanced-http-timeout"
                      placeholder="Default LBAC rule"
                      aria-label="Default LBAC rule"
                      value={defaultRule}
                      onChange={onDefaultRuleChange}
                    />
                  </InlineField>
                  <Button
                    disabled={!canEdit}
                    variant="primary"
                    aria-label="Save default rule"
                    onClick={onSaveDefaultRule}
                  >
                    Save
                  </Button>
                </InlineFieldRow>
              </div>
            </ConfigSubSection>
          </HorizontalGroup>
          <ConfigSubSection
            title="Team LBAC rules"
            description={
              <ConfigDescriptionLink
                description="Apply a specific rule for a team."
                suffix="loki/configure-loki-data-source/#team-lbac-rules"
                feature="team lbac rules"
              />
            }
          >
            {canEdit && (
              <HorizontalGroup>
                <Button onClick={() => setShowLBACForm(true)}>Add a team LBAC rule</Button>
              </HorizontalGroup>
            )}
            {showLBACForm && <AddTeamLBACForm onSubmit={onSubmitLBAC} onClose={() => setShowLBACForm(false)} />}
          </ConfigSubSection>
          {teamsHeaders.length !== 0 && (
            <div className={styles.wrapper}>
              <div>
                <table role="grid" className="filter-table gf-form-group" aria-labelledby="team_lbac_rules">
                  <thead>
                    <tr>
                      <th style={{ width: '30%' }}>Team</th>
                      <th>Label selector</th>
                      <th style={{ width: '1%' }} />
                      <th style={{ width: '1%' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {teamsHeaders.map((item, index) => (
                      <tr key={index} role="row">
                        <td role="gridcell" aria-labelledby={`team_label_${item.id}`}>
                          <div className={styles.teamPicker}>
                            <TeamLabel
                              team={teams.find((t) => t.id === item.teamId) || { id: item.teamId }}
                              disabled={!canEdit}
                            />
                          </div>
                        </td>
                        <td>
                          <EditableCell
                            value={item.labelSelector}
                            row={item}
                            prop="labelSelector"
                            disabled={!canEdit}
                            isEditing={rowsEditing[item.id]}
                            onChange={(value) => onEditRow(item.id, { ...item, labelSelector: value })}
                            error={rowsError[item.id]}
                          />
                        </td>
                        <td>
                          {canEdit ? (
                            !rowsEditing[item.id] ? (
                              <div className={styles.editButton}>
                                <IconButton
                                  name="pen"
                                  aria-label="edit team header"
                                  onClick={() => {
                                    setRowsEditing({ ...rowsEditing, [item.id]: true });
                                  }}
                                />
                              </div>
                            ) : (
                              <HorizontalGroup>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    onSaveHeader(item);
                                  }}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => {
                                    setRowsEditing({ ...rowsEditing, [item.id]: false });
                                  }}
                                >
                                  Cancel
                                </Button>
                              </HorizontalGroup>
                            )
                          ) : null}
                        </td>
                        <td>
                          <DeleteButton
                            aria-label={'Delete rule'}
                            size="sm"
                            disabled={!canEdit}
                            onConfirm={() => onDeleteHeader(item.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </ConfigSection>
      </VerticalGroup>
    </div>
  );
};

const getStyles = (theme: GrafanaTheme2) => {
  return {
    wrapper: css({
      display: 'flex',
      flexDirection: 'column',
      overflowX: 'auto',
      overflowY: 'hidden',
      minHeight: '100vh',
      width: '100%',
      '& > div': {
        overflowX: 'unset',
        marginBottom: theme.spacing(2),
      },
      '& > td': {
        padding: theme.spacing(1), // Adjust the padding as needed
      },
    }),
    sectionHeader: css({
      marginBottom: theme.spacing(2),
    }),
    teamPicker: css({
      width: theme.spacing(30),
    }),
    editButton: css({
      display: 'flex',
      alignItems: 'center',
    }),
    container: css`
      width: 100%;
    `,
    row: css`
      align-items: baseline;
    `,
  };
};
